import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup, signOut } from 'firebase/auth';
import { apiCall } from './api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [authState, setAuthState] = useState(() => {
        const storedAuth = sessionStorage.getItem('authState');
        if (storedAuth) {
            console.log('[AuthContext] Restoring authState');
            return JSON.parse(storedAuth);
        }
        return {
            userId: null,
            userName: '',
            userEmail: '',
            accessToken: '',
            supabaseRefreshToken: '',
        };
    });

    useEffect(() => {
        if (authState && authState.accessToken) {
            console.log('[AuthContext] Saving authState');
            sessionStorage.setItem('authState', JSON.stringify(authState));
        } else {
            console.log('[AuthContext] Clearing authState');
            sessionStorage.removeItem('authState');
        }
    }, [authState]);

    const signInWithGoogle = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            console.log('[AuthContext] Google sign-in successful');
            setAuthState((prev) => ({
                ...prev,
                userName: result.user.displayName || '',
                userEmail: result.user.email || '',
                accessToken: '',
            }));
            return result;
        } catch (error) {
            console.error('[AuthContext] Sign-in failed');
            throw new Error('Authentication failed');
        }
    };

    const setAuthData = (userId, userName, accessToken, supabaseRefreshToken, userEmail, callback) => {
        console.log('[AuthContext] Setting auth data');
        setAuthState({
            userId: Number(userId),
            userName,
            userEmail,
            accessToken,
            supabaseRefreshToken,
        });
        if (callback) callback();
    };

    const clearAuthData = () => {
        console.log('[AuthContext] Clearing auth data');
        setAuthState({
            userId: null,
            userName: '',
            userEmail: '',
            accessToken: '',
            supabaseRefreshToken: '',
        });
    };

    const signOutUser = async () => {
        try {
            console.log('[AuthContext] Signing out');
            await signOut(auth);
            clearAuthData();
        } catch (error) {
            console.error('[AuthContext] Sign-out failed');
            throw error;
        }
    };

    const refreshPromiseRef = useRef(null);

    const refreshAccessToken = useCallback(async () => {
        if (refreshPromiseRef.current) {
            return refreshPromiseRef.current;
        }

        const refreshToken = authState?.supabaseRefreshToken;
        if (!refreshToken) {
            throw new Error('No refresh token available. Please sign in again.');
        }

        const refreshTask = (async () => {
            try {
                console.log('[AuthContext] Refreshing access token');
                const response = await apiCall(
                    '/auth/refresh',
                    'POST',
                    { refresh_token: refreshToken },
                    true
                );

                const newAccessToken = response?.access_token;
                const newRefreshToken = response?.refresh_token || response?.supabase_refresh_token;

                if (!newAccessToken) {
                    throw new Error('Failed to refresh access token');
                }

                setAuthState((prev) => ({
                    ...prev,
                    accessToken: newAccessToken,
                    supabaseRefreshToken: newRefreshToken || prev.supabaseRefreshToken,
                }));

                return newAccessToken;
            } catch (error) {
                console.error('[AuthContext] Access token refresh failed:', error);
                throw error;
            } finally {
                refreshPromiseRef.current = null;
            }
        })();

        refreshPromiseRef.current = refreshTask;
        return refreshTask;
    }, [authState?.supabaseRefreshToken]);

    return (
        <AuthContext.Provider value={{ authState, signInWithGoogle, setAuthData, clearAuthData, signOut: signOutUser, refreshAccessToken }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}